import { supabase } from "./backendService";
import { generateEmbedding } from "./geminiService";
import { WisdomShlok } from "../types";
import logger from "@/utils/logger";

/**
 * Helper to clean PDF filenames for display.
 * "bhagavad_gita_sivananda.pdf" -> "Bhagavad Gita (Sivananda)"
 */
const formatBookName = (name: string): string => {
  if (!name) return "Ancient Scripture";
  if (!name.toLowerCase().endsWith('.pdf')) return name;

  // Strip extension
  let cleanName = name.replace(/\.pdf$/i, '');
  
  // Replace underscores/hyphens with spaces
  cleanName = cleanName.replace(/[_-]/g, ' ');

  // Handle specific patterns like "sivananda" if they are part of the filename
  // This is a simple heuristic: if it contains (sivananda), wrap it in parens
  if (cleanName.toLowerCase().includes('sivananda')) {
    cleanName = cleanName.replace(/sivananda/i, '(Sivananda)');
  }

  // Title Case
  return cleanName
    .split(' ')
    .map(word => {
      if (word.startsWith('(')) return word.charAt(0) + word.charAt(1).toUpperCase() + word.slice(2).toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Cleans the RAG content by removing redundant "Source:" headers 
 * and ensuring only one primary entry exists in the card.
 */
const cleanShlokContent = (content: string): string => {
  if (!content) return "";

  // 1. Remove common redundant citation patterns (especially from RAG imports)
  // Matches "Source: "The Upanishads..." or just "Source: ..." until end of line
  let cleaned = content.replace(/^Source:\s*["'].*?["'].*?\d*$/mi, '');
  cleaned = cleaned.replace(/^Source:.*$/mi, '');
  
  // 2. Remove specific book citations that often appear as headers in chunks
  cleaned = cleaned.replace(/^"The Upanishads - A New Translation".*$/mi, '');
  cleaned = cleaned.replace(/^Chapter\s+[IVXLCDM]+\s*[-—].*$/mi, '');
  
  // 3. Remove leading Verse indices if they are just standalone numbers on the first line
  cleaned = cleaned.trim().replace(/^\d+[\s\n\r]/, '');

  // 4. Truncate to ONE entry
  // If we detect a clear start of a "new" segment (e.g., another Source: or another Chapter), 
  // we take everything BEFORE that.
  const secondaryStart = cleaned.search(/\n\s*Source:|\n\s*Chapter\s+[IVXLCDM]+/i);
  if (secondaryStart !== -1) {
    cleaned = cleaned.substring(0, secondaryStart);
  }

  // 5. Final sanitation: Limit length to keep cards focused (approx 650 chars)
  if (cleaned.length > 650) {
    // Try to truncate at a paragraph break or sentence first
    const paragraphBreak = cleaned.lastIndexOf('\n\n', 650);
    if (paragraphBreak > 200) {
      cleaned = cleaned.substring(0, paragraphBreak);
    } else {
      const sentenceEnd = cleaned.lastIndexOf('.', 650);
      if (sentenceEnd > 200) {
        cleaned = cleaned.substring(0, sentenceEnd + 1);
      } else {
        cleaned = cleaned.substring(0, 650) + "...";
      }
    }
  }

  return cleaned.trim();
};

/**
 * Service to fetch wisdom shloks from Supabase documents_gemini table.
 * Supports both random "Daily Wisdom" and semantically relevant shloks via RAG.
 */
export const fetchWisdomShloks = async (query?: string): Promise<WisdomShlok[]> => {
  try {
    if (query && query.trim() !== "") {
      logger.log(`[WisdomService] Fetching contextual shloks for: "${query}"`);
      
      // 1. Generate embedding for semantic search
      const queryVector = await generateEmbedding(query);
      if (!queryVector) {
        throw new Error("Failed to generate embedding for wisdom search");
      }

      // 2. Call match_wisdom_documents RPC (fetch 5 candidates for re-ranking)
      const { data, error } = await supabase.rpc('match_wisdom_documents', {
        query_embedding: queryVector,
        match_threshold: 0.3,
        match_count: 5 
      });

      if (error) throw error;
      
      const results = (data || []).map((item: any) => ({
        id: item.id,
        source_text: formatBookName(item.book_name),
        chapter_verse: item.verse_reference || "Vedic Tradition",
        content: cleanShlokContent(item.content), // Cleaned
        kosha_layer: (item.kosha_layer as any) || 'vijnanamaya',
        life_guidance: item.life_guidance || null,
        tags: [],
        raw_book_name: item.book_name,
        raw_verse_reference: item.verse_reference
      }));

      // 3. Client-side re-ranking: Prioritize curated verses
      const curated = results.filter((r: any) => 
        r.raw_verse_reference && !r.raw_book_name.toLowerCase().endsWith('.pdf')
      );
      const pdfChunks = results.filter((r: any) => 
        !r.raw_verse_reference || r.raw_book_name.toLowerCase().endsWith('.pdf')
      );

      const finalResults = [
        ...curated.slice(0, 3),
        ...pdfChunks.slice(0, Math.max(0, 3 - curated.length))
      ].slice(0, 3);

      // Recursive Fallback: if semantic search returns nothing, fetch general wisdom
      if (finalResults.length === 0) {
        logger.log(`[WisdomService] No semantic matches for "${query}" — falling back to general Daily Wisdom`);
        return fetchWisdomShloks(); 
      }

      return finalResults;
    } else {
      logger.log("[WisdomService] Fetching default Daily Wisdom shloks (Restored Schema)");
      
      const { data, error } = await supabase
        .from('documents_gemini')
        .select('*') // Safest to get all and map locally
        .eq('source', 'Wisdom')
        .limit(10); 

      if (error) {
        logger.error("[WisdomService] Fallback fetch failed:", error);
        throw error;
      }
      
      // Shuffle locally for variety
      const shuffled = (data || []).sort(() => 0.5 - Math.random());
      
      return shuffled.slice(0, 3).map((item: any) => {
        // Handle metadata which might be a JSON object or string or missing
        const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : (item.metadata || {});
        
        return {
          id: item.id,
          source_text: formatBookName(item.book_name),
          chapter_verse: metadata.verse_reference || item.verse_reference || "Vedic Tradition",
          content: cleanShlokContent(item.content), // Cleaned
          kosha_layer: (item.kosha_layer as any) || 'vijnanamaya',
          life_guidance: metadata.life_guidance || item.life_guidance || null,
          tags: metadata.tags || item.tags || []
        };
      });
    }
  } catch (e) {
    logger.error("[WisdomService] Error in fetchWisdomShloks:", e);
    throw e;
  }
};
